import { HealthBar } from '@appd/uik';
import Link from 'next/link';
import { HealthStatus, getHealthStatusDef } from '../health';

const getHealthCss = (status: number) => ({
  backgroundColor: getHealthStatusDef(status).color,
  height: 8,
  width: 8,
});

const handleMouseEnter = (tooltipText: string) => (event: React.MouseEvent<HTMLDivElement>) => {
  // Ensure that the target is an instance of HTMLElement to access setAttribute
  if (event.target instanceof HTMLElement) {
    event.target.setAttribute('title', tooltipText);
  }
};

export interface EntityHealth {
  id: string;
  name?: string;
  status: number;
}

export interface HealthProps {
  data: EntityHealth[];
  showAsRatioBar: boolean;
  showAsTiles: boolean;
  since: string;
  until: string;
}

const SINCE_PARAM = 'since';
const UNTIL_PARAM = 'until';
const getEntityURL = (id: string) => {
  return `${process.env.NEXT_PUBLIC_TENANT_OBSERVE_BASE_PATH}/${id.split(':').map(encodeURIComponent).join('/')}`;
};

export const Health = ({
  data,
  showAsRatioBar = false,
  showAsTiles = true,
  since,
  until,
}: HealthProps): JSX.Element => {
  const healthCounts: number[] = [0, 0, 0, 0];

  Array.isArray(data) &&
    data.forEach(health => {
      healthCounts[health.status !== null ? health.status : 1]++;
    });

  const urlParams: string[] = [];
  since && urlParams.push(`${SINCE_PARAM}=${encodeURIComponent(since.replace(/\s/g, ''))}`);
  until && urlParams.push(`${UNTIL_PARAM}=${encodeURIComponent(until.replace(/\s/g, ''))}`);

  return !Array.isArray(data) ? (
    <div>No Data</div>
  ) : (
    <div>
      {showAsRatioBar && (
        <HealthBar
          style={{ fontSize: '12px', margin: '10px 0 10px' }}
          segments={[
            {
              status: {
                color: getHealthStatusDef(HealthStatus.CRITICAL).color,
                name: 'Critical',
              },
              value: (healthCounts[HealthStatus.CRITICAL] / data.length) * 100,
            },
            {
              status: {
                color: getHealthStatusDef(HealthStatus.WARNING).color,
                name: 'Warning',
              },
              value: (healthCounts[HealthStatus.WARNING] / data.length) * 100,
            },
            {
              status: {
                color: getHealthStatusDef(HealthStatus.UNKNOWN).color,
                name: 'Unknown',
              },
              value: (healthCounts[HealthStatus.UNKNOWN] / data.length) * 100,
            },
            {
              status: {
                color: getHealthStatusDef(HealthStatus.NORMAL).color,
                name: 'Normal',
              },
              value: (healthCounts[HealthStatus.NORMAL] / data.length) * 100,
            },
          ]}
          showPercentSign
          decimalPlaces={0}
        />
      )}
      {showAsTiles && (
        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 3 }}>
          {data
            .sort((a, b) => (b.status ?? 1) - (a.status ?? 1))
            .map(row => {
              return (
                <Link key={row.id} href={`${getEntityURL(row.id)}?${urlParams.join('&')}`}>
                  <div style={getHealthCss(row.status)} onMouseEnter={handleMouseEnter(row.name ?? '-')} />
                </Link>
              );
            })}
        </div>
      )}
    </div>
  );
};
